import React, { ComponentProps, forwardRef } from "react";
import { styled } from "~/application/theme";
import { Icon } from "../Icon";

export type ButtonProps = ComponentProps<typeof ButtonRoot>;

const ButtonRoot = styled("button", {
  // Reset
  all: "unset",
  alignItems: "center",
  boxSizing: "border-box",
  userSelect: "none",
  whiteSpace: "nowrap",

  "&::before": {
    boxSizing: "border-box",
  },
  "&::after": {
    boxSizing: "border-box",
  },

  // Custom reset?
  display: "inline-flex",
  flexShrink: 0,
  justifyContent: "center",
  lineHeight: "1",
  WebkitTapHighlightColor: "rgba(0,0,0,0)",

  // Custom
  fontWeight: 600,
  fontSize: "$md",
  fontVariantNumeric: "tabular-nums",
  borderRadius: "$md",
  transition: "$normal",
  gap: "$2",

  "&:disabled": {
    cursor: "not-allowed",
    opacity: "$disabled",
  },

  "&:not(:disabled):hover": {
    cursor: "pointer",
  },

  "&:focus": {
    boxShadow: "$focus-md",
  },

  variants: {
    size: {
      xs: {
        height: "$8",
        padding: "0 $2",
        fontSize: "$xs",
        lineHeight: "$2",

        [`& ${Icon}`]: {
          size: "$4",
        },
      },
      sm: {
        height: "$11",
        padding: "0 $4",
        fontSize: "$md",
        lineHeight: "$4",

        [`& ${Icon}`]: {
          size: "$5",
        },

        "@mxlg": {
          height: "$10",
          fontSize: "$sm",
        },
      },
      md: {
        height: "$12",
        padding: "0 $4",
        fontSize: "$md",
        lineHeight: "$5",

        [`& ${Icon}`]: {
          size: "$5",
        },

        "@mxlg": {
          height: "$10",
          fontSize: "$sm",
          lineHeight: "$4",
        },
      },
      mdl: {
        height: "$16",
        padding: "0 $4",
        fontSize: "$lg",
        lineHeight: "$5",

        [`& ${Icon}`]: {
          size: "$5",
        },
      },
      lg: {
        height: "$20",
        padding: "0 $6",
        fontSize: "$xl",
        lineHeight: "$6",

        [`& ${Icon}`]: {
          size: "$8",
        },
      },
    },
    variant: {
      none: {
        background: "none",
        padding: "0",
        border: "none",

        "&:not(:disabled):hover": {
          cursor: "auto",
        },

        "&:focus": {
          boxShadow: "none",
        },
      },
      primary: {
        backgroundColor: "$primary-600",
        color: "$neutrals-white",
        fill: "$neutrals-white",

        "&:not(:disabled):hover": {
          backgroundColor: "$primary-700",
        },
      },
      "primary-700": {
        backgroundColor: "$primary-700",
        color: "$neutrals-white",
        fill: "$neutrals-white",

        "&:not(:disabled):hover": {
          backgroundColor: "$primary-800",
        },
      },
      "primary-outline": {
        backgroundColor: "transparent",
        color: "$primary-600",
        fill: "$primary-600",
        border: "2px solid $primary-600",

        "&:not(:disabled):hover": {
          backgroundColor: "$primary-100",
        },
      },
      "primary-light": {
        backgroundColor: "$neutrals-white",
        color: "$primary-600",
        fill: "$primary-600",
        border: "2px solid $primary-600",

        "&:not(:disabled):hover": {
          backgroundColor: "$primary-600",
          color: "$neutrals-white",
          fill: "$neutrals-white",
        },
      },
      "secondary-light": {
        backgroundColor: "transparent",
        color: "$neutrals-darkest",
        fill: "$neutrals-darkest",
        border: "2px solid $neutrals-darkest",

        "&:not(:disabled):hover": {
          backgroundColor: "$neutrals-black",
          color: "$neutrals-white",
          fill: "$neutrals-white",
        },

        "&:active": {
          backgroundColor: "$neutrals-black",
          color: "$neutrals-white",
        },
      },
      secondary: {
        backgroundColor: "$neutrals-darkest",
        color: "$neutrals-white",
        fill: "$neutrals-white",

        "&:not(:disabled):hover": {
          backgroundColor: "$neutrals-black",
        },
      },
      "secondary-outline": {
        backgroundColor: "transparent",
        color: "$neutral-600",
        fill: "$neutral-600",
        border: "2px solid $neutral-600",

        "&:not(:disabled):hover": {
          backgroundColor: "$neutral-200",
        },
      },
      warning: {
        color: "$warning-dark",
        backgroundColor: "rgba(255, 237, 216, 1)",
        fill: "$warning-dark",

        "&:not(:disabled):hover": {
          backgroundColor: "$warning-base",
        },
      },
      "warning-light": {
        color: "#DA763A",
        backgroundColor: "$warning-light",
        fill: "$warning-dark",
        border: "2px solid #DA763A",

        "&:not(:disabled):hover": {
          backgroundColor: "$warning-base",
        },

        "&:focus": {
          boxShadow: "none",
        },
      },
      tertiary: {
        backgroundColor: "$neutral-200",
        color: "$neutrals-black",
        fill: "$neutrals-black",

        "&:not(:disabled):hover": {
          backgroundColor: "$neutral-300",
        },
      },
      success: {
        backgroundColor: "$success-base",
        color: "$success-light",
        fill: "$success-base",
        opacity: 0.95,

        "&:not(:disabled):hover": {
          opacity: 1,
        },
      },
      "success-light": {
        color: "$success-base",
        backgroundColor: "$success-light",
        fill: "$success-base",
        border: "2px solid $success-base",

        "&:not(:disabled):hover": {
          backgroundColor: "#ddf1db",
        },

        "&:focus": {
          boxShadow: "none",
        },
      },
      error: {
        fill: "$neutrals-white",
        color: "$neutrals-white",
        backgroundColor: "$error-base",
        opacity: 0.85,
        "&:not(:disabled):hover": {
          opacity: 1,
        },
      },
      "error-outline": {
        backgroundColor: "transparent",
        color: "$danger-600",
        fill: "$danger-600",
        border: "2px solid $danger-600",

        "&:not(:disabled):hover": {
          backgroundColor: "$danger-100",
        },
      },
      "error-light": {
        color: "$error-base",
        backgroundColor: "$error-light",
        fill: "$error-base",
        border: "2px solid $error-base",

        "&:not(:disabled):hover": {
          backgroundColor: "#f9d1db",
        },

        "&:focus": {
          boxShadow: "none",
        },
      },
      "info-light": {
        color: "$primary-600",
        backgroundColor: "$primary-50",
        fill: "$primary-600",
        border: "2px solid $primary-600",

        "&:not(:disabled):hover": {
          backgroundColor: "$primary-100",
        },

        "&:focus": {
          boxShadow: "none",
        },
      },
      outline: {
        backgroundColor: "$neutrals-white",
        border: "2px solid $colors$neutrals-light",
        color: "$neutrals-darkest",
        fill: "$neutrals-darkest",

        "&:not(:disabled):hover": {
          backgroundColor: "$neutrals-lightest",
        },
      },
    },
    ghost: {
      true: {
        backgroundColor: "transparent",
        boxShadow: "none",
        border: "none",
      },
    },
    rounded: {
      true: {
        borderRadius: "$pill",
      },
    },
    fullWidth: {
      true: {
        width: "100%",
      },
    },
    mode: {
      align: {
        width: "53%",
        margin: "0 auto",
        alignItems: "center",
      },
    },
  },

  defaultVariants: {
    variant: "primary",
    size: "sm",
  },
});

export const Button = forwardRef<React.ElementRef<typeof ButtonRoot>, ButtonProps>(
  (props, forwardedRef) => {
    return <ButtonRoot type="button" {...props} ref={forwardedRef} />;
  },
);

Button.displayName = "Button";
